import { useCallback, useMemo } from 'react';
import compact from 'lodash/compact';
import { OrderingItem } from '@/models/Answer';
import { FlowColumn, QuestionTypeEnum } from '@/models/Question';
import { QuestionViewPropsFor } from '@/components/question/view/types';
import { Flow } from '@/utils/questions/dnd/types';
import { hashMap } from '@/utils/shared/hashMap';

export type OrderingCorrectionStatus = 'correct' | 'incorrect';

export type OrderingCorrectionEntry = {
  id: number;
  position: number;
  item: OrderingItem;
  status: OrderingCorrectionStatus;
};

export function useOrderingCorrection(
  props: QuestionViewPropsFor<QuestionTypeEnum.DND_ORDENATION>,
) {
  const { question, value } = props;
  const column = question.columns.at(0);

  const itemById = hashMap(column?.items ?? [], item => String(item.id));

  const isCorrect = useCallback((item: OrderingItem, position: number) => {
    return item.validPositions.includes(position) ? 'correct' : 'incorrect';
  }, []);

  const entries = useMemo<OrderingCorrectionEntry[]>(() => {
    const sequence = value ?? [];
    return compact(
      sequence.map((id, index) => {
        const item = itemById.get(String(id));
        if (!item) return undefined;
        const position = index + 1;
        const status = isCorrect(item, position);
        return { id: item.id, position, item, status };
      }),
    );
  }, [value, itemById, isCorrect]);

  const flow: Flow =
    column?.flow === FlowColumn.VERTICAL ? 'vertical' : 'horizontal';

  return { entries, flow };
}
